import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Upload, Download, RotateCcw, AlertTriangle, CheckCircle2,
    FileSpreadsheet, ChevronDown, ChevronUp, ArrowLeft, Loader2, X,
    Eye, Film, Target, Layers, Zap, Map, LogOut, BarChart3, FileText, MessageSquare
} from 'lucide-react';
import {
    parseXlsx, saveContent, resetContent,
    downloadEmptyTemplate, exportCurrentAsXlsx, useContent
} from '../services/contentService';
import { fetchAdminContent } from '../services/adminApi';
import { getStoredAdminPassword, clearStoredAdminPassword } from '../services/adminAuth';
import AdminLogin from './AdminLogin';
import AdminSurveysTable from './AdminSurveysTable';
import AdminSessionsTable from './AdminSessionsTable';

/** Convierte un bloque `es`/`en` del backend a la forma que usa la previsualización. */
function unpackBackendLocaleBundle(b) {
    if (!b) {
        return {
            skills: {}, criteria: {}, challenges: {}, escenarios: {}, sceneMoments: {},
        };
    }
    const sc = b.skill_config || {};
    const skills = {};
    const criteria = {};
    const challenges = {};
    Object.entries(sc).forEach(([slug, cfg]) => {
        const k = cfg.skill_key;
        if (k) skills[k] = { nombre: cfg.nombre, slug, descripcion: cfg.descripcion || '' };
        if (cfg.criteria && Object.keys(cfg.criteria).length) criteria[slug] = cfg.criteria;
        const ch = cfg.challenge_templates;
        if (ch && ch.length) challenges[slug] = ch;
    });
    let escenarios = b.escenarios || {};
    if (Array.isArray(escenarios)) {
        escenarios = Object.fromEntries(
            escenarios.filter(e => e && e.id).map(e => {
                const { id, ...rest } = e;
                return [id, rest];
            }),
        );
    }
    return {
        skills,
        criteria,
        challenges,
        escenarios,
        sceneMoments: b.scene_moments || {},
    };
}

/** Bloque de secciones plegables para un idioma (previsualización admin). */
function LocaleContentSections({ unpack, emptyNote }) {
    const { skills, criteria, challenges, escenarios, sceneMoments } = unpack;
    const skillCount = Object.keys(skills).length;
    const critCount = Object.values(criteria).reduce((a, c) => a + Object.keys(c).length, 0);
    const chalCount = Object.values(challenges).reduce((a, arr) => a + arr.length, 0);
    const escCount = Object.keys(escenarios).length;
    const smCount = Object.values(sceneMoments).reduce((a, arr) => a + (arr?.length || 0), 0);

    return (
        <div className="space-y-2">
            <Section icon={Target} title="Skills" badge={skillCount}>
                {!skillCount ? emptyNote('skills') : (
                    <div className="space-y-2">
                        {Object.entries(skills).map(([key, s]) => (
                            <div key={key} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/30">
                                <p className="text-xs font-medium text-slate-200">{s.nombre || key}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5 font-mono">{s.slug || key}</p>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
            <Section icon={Layers} title="Criterios" badge={critCount}>
                {!critCount ? emptyNote('criterios') : (
                    <div className="space-y-3">
                        {Object.entries(criteria).map(([slug, cmap]) => (
                            <div key={slug}>
                                <p className="text-[11px] font-mono text-indigo-400 mb-1.5">{slug}</p>
                                <div className="space-y-1">
                                    {Object.entries(cmap).map(([ckey, c]) => (
                                        <div key={ckey} className="px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/30">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-slate-300 font-mono">{ckey}</span>
                                                <span className="text-[10px] text-slate-500">nivel mín. {c.nivel_minimo}</span>
                                            </div>
                                            <ul className="mt-1 space-y-0.5">
                                                {(c.indicadores || []).map((ind, i) => (
                                                    <li key={i} className="text-[11px] text-slate-500 pl-2 before:content-['·'] before:mr-1.5">{ind}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
            <Section icon={Zap} title="Challenges" badge={chalCount}>
                {!chalCount ? emptyNote('challenges') : (
                    <div className="space-y-3">
                        {Object.entries(challenges).map(([slug, chs]) => (
                            <div key={slug}>
                                <p className="text-[11px] font-mono text-indigo-400 mb-1.5">{slug}</p>
                                <div className="space-y-1">
                                    {chs.map((ch, i) => (
                                        <div key={i} className="px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/30">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs text-slate-300">{ch.label}</span>
                                                <span className="text-[10px] text-slate-500">nivel {ch.nivel}</span>
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{ch.template}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
            <Section icon={Map} title="Escenarios" badge={escCount}>
                {!escCount ? emptyNote('escenarios') : (
                    <div className="space-y-2">
                        {Object.entries(escenarios).map(([id, esc]) => (
                            <div key={id} className="px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-700/30">
                                <p className="text-xs font-medium text-slate-200">{esc.nombre}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">{esc.tipo_proyecto}</p>
                                <p className="text-[11px] text-slate-600 mt-1 line-clamp-2">{esc.descripcion_breve}</p>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
            <Section icon={Film} title="Scene Moments" badge={smCount}>
                {!smCount ? (
                    <p className="text-xs text-slate-500 italic">No hay scene moments — el backend puede usar lógica interna.</p>
                ) : (
                    <div className="space-y-3">
                        {Object.entries(sceneMoments).map(([stage, moments]) => (
                            <div key={stage}>
                                <p className="text-[11px] font-mono text-amber-400/80 mb-1.5">{stage}</p>
                                <div className="space-y-1">
                                    {(moments || []).map((m, i) => (
                                        <div key={i} className="px-3 py-1.5 rounded-lg bg-slate-900/60 border border-slate-700/30">
                                            <div className="flex items-center justify-between">
                                                <span className="text-xs font-mono text-slate-300">{m.id}</span>
                                                {m.next_stage && (
                                                    <span className="text-[10px] text-slate-500">→ {m.next_stage}</span>
                                                )}
                                            </div>
                                            <p className="text-[11px] text-slate-400 mt-0.5 italic">"{m.text}"</p>
                                            {Array.isArray(m.triggers) && m.triggers.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {m.triggers.map((tr, ti) => (
                                                        <span key={ti} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 border border-slate-700/30">{tr}</span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}

// ─── Componente de badge de origen ──────────────────────────────────────────
function SourceBadge({ source }) {
    if (source === 'backend') return (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">backend</span>
    );
    if (source === 'none') return (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/40 text-slate-500 border border-slate-600/20">sin definir</span>
    );
    return (
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/60 text-slate-400 border border-slate-600/30">fallback</span>
    );
}

// ─── Panel de resumen del .xlsx parseado ─────────────────────────────────────
const SUMMARY_FIELDS = [
    { key: 'skills',       label: 'Skills',        icon: '🎯' },
    { key: 'criteria',     label: 'Criterios',     icon: '📋' },
    { key: 'challenges',   label: 'Challenges',    icon: '⚡' },
    { key: 'escenarios',   label: 'Escenarios',    icon: '🏭' },
    { key: 'sceneMoments', label: 'Scene moments', icon: '🎬' },
];

function SummaryGrid({ data }) {
    return (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {SUMMARY_FIELDS.map(({ key, label, icon }) => (
                <div key={key} className="flex flex-col items-center justify-center p-3 rounded-lg bg-slate-900/60 border border-slate-700/30">
                    <span className="text-xl mb-1">{icon}</span>
                    <span className="text-2xl font-bold text-white">{data[key] ?? 0}</span>
                    <span className="text-[11px] text-slate-500 mt-0.5">{label}</span>
                </div>
            ))}
        </div>
    );
}

function ParseSummary({ summary, errors, hasBlockingErrors, bilingual }) {
    const [open, setOpen] = useState(true);
    return (
        <div className="rounded-xl border border-slate-700/40 overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-5 py-3.5 bg-slate-800/50 hover:bg-slate-800/70 transition-colors"
            >
                <span className="text-sm font-medium text-slate-200">Resumen del archivo</span>
                {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
            </button>
            {open && (
                <div className="p-4 space-y-3">
                    {bilingual && summary.es && summary.en ? (
                        <div className="space-y-3">
                            {['es', 'en'].map(lang => (
                                <div key={lang}>
                                    <p className="text-[11px] font-semibold text-indigo-300 uppercase tracking-wider mb-2">{lang}</p>
                                    <SummaryGrid data={summary[lang]} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <SummaryGrid data={summary} />
                    )}
                    {errors.length > 0 && (
                        <div className="space-y-1.5 mt-2">
                            <p className="text-[11px] uppercase tracking-widest text-slate-500 font-medium">Advertencias / Errores</p>
                            {errors.map((e, i) => (
                                <div key={i} className={`flex items-start gap-2 text-xs rounded-lg px-3 py-2 ${
                                    hasBlockingErrors && (e.includes('columnas') || e.includes('vacío'))
                                        ? 'bg-red-500/10 text-red-300 border border-red-500/20'
                                        : 'bg-amber-500/10 text-amber-300 border border-amber-500/20'
                                }`}>
                                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                                    {e}
                                </div>
                            ))}
                        </div>
                    )}
                    {errors.length === 0 && (
                        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-3 py-2">
                            <CheckCircle2 className="w-4 h-4" />
                            Archivo válido sin errores
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// ─── Previsualización del contenido publicado ────────────────────────────────
function Section({ icon: Icon, title, badge, children }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="rounded-xl border border-slate-700/40 overflow-hidden">
            <button
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between px-4 py-3 bg-slate-800/40 hover:bg-slate-800/60 transition-colors"
            >
                <div className="flex items-center gap-2.5">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-300">{title}</span>
                    {badge != null && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-500/15 text-indigo-300 border border-indigo-500/20">
                            {badge}
                        </span>
                    )}
                </div>
                {open ? <ChevronUp className="w-4 h-4 text-slate-600" /> : <ChevronDown className="w-4 h-4 text-slate-600" />}
            </button>
            {open && <div className="p-4">{children}</div>}
        </div>
    );
}

// ContentPreview: llama directamente a GET /api/management/content para
// mostrar exactamente lo que hay en el blob, sin mezclar con el fallback.
function ContentPreview({ onRefreshDone, experience }) {
    const [raw, setRaw] = useState(null);       // respuesta cruda del backend
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAdminContent({ experience });
            setRaw(data);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [experience]);

    useEffect(() => { load(); }, [load]);

    // Exponer recarga al padre (se llama tras publicar o resetear)
    useEffect(() => {
        if (onRefreshDone) onRefreshDone(load);
    }, [onRefreshDone, load]);

    // ── conteos sobre el JSON crudo (bilingüe o legado) ─────────────────────
    const isBilingualBlob = raw?.es && typeof raw.es === 'object';

    let skills; let criteria; let challenges; let escenarios; let sceneMoments;
    let skillCount; let critCount; let chalCount; let escCount; let smCount; let totalItems;
    let localeUnpack = null;

    if (isBilingualBlob) {
        const uEs = unpackBackendLocaleBundle(raw.es);
        const uEn = unpackBackendLocaleBundle(raw.en);
        localeUnpack = { es: uEs, en: uEn };
        const countU = (u) => ({
            sk: Object.keys(u.skills).length,
            cr: Object.values(u.criteria).reduce((a, c) => a + Object.keys(c).length, 0),
            ch: Object.values(u.challenges).reduce((a, arr) => a + arr.length, 0),
            es: Object.keys(u.escenarios).length,
            sm: Object.values(u.sceneMoments).reduce((a, arr) => a + (arr?.length || 0), 0),
        });
        const cEs = countU(uEs);
        const cEn = countU(uEn);
        skillCount = cEs.sk + cEn.sk;
        critCount = cEs.cr + cEn.cr;
        chalCount = cEs.ch + cEn.ch;
        escCount = cEs.es + cEn.es;
        smCount = cEs.sm + cEn.sm;
        totalItems = skillCount + critCount + chalCount + escCount + smCount;
        skills = uEs.skills;
        criteria = uEs.criteria;
        challenges = uEs.challenges;
        escenarios = uEs.escenarios;
        sceneMoments = uEs.sceneMoments;
    } else {
        skills       = raw?.skills       || {};
        criteria     = raw?.criteria     || {};
        challenges   = raw?.challenges   || {};
        escenarios   = raw?.escenarios   || {};
        sceneMoments = raw?.scene_moments || {};
        if (raw?.skill_config && Object.keys(raw.skill_config).length && !Object.keys(skills).length) {
            const u = unpackBackendLocaleBundle(raw);
            skills = u.skills;
            criteria = u.criteria;
            challenges = u.challenges;
            if (!Object.keys(escenarios).length && raw.escenarios) {
                escenarios = u.escenarios;
            }
            if (!Object.keys(sceneMoments).length) sceneMoments = u.sceneMoments;
        }
        if (Array.isArray(escenarios)) {
            escenarios = unpackBackendLocaleBundle({ escenarios: raw.escenarios }).escenarios;
        }
        skillCount  = Object.keys(skills).length;
        critCount   = Object.values(criteria).reduce((a, c) => a + Object.keys(c).length, 0);
        chalCount   = Object.values(challenges).reduce((a, arr) => a + arr.length, 0);
        escCount    = Object.keys(escenarios).length;
        smCount     = Object.values(sceneMoments).reduce((a, arr) => a + (arr?.length || 0), 0);
        totalItems  = skillCount + critCount + chalCount + escCount + smCount;
    }

    const isEmpty = totalItems === 0 && !loading;

    const emptyNote = (label) => (
        <p className="text-xs text-slate-500 italic">No hay {label} publicados en el backend.</p>
    );

    return (
        <div className="rounded-xl border border-slate-700/40 bg-slate-900/50 p-5">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                    <Eye className="w-4 h-4 text-slate-500" />
                    Contenido en el backend
                    <span className="text-[10px] text-slate-600 font-normal">GET /management/content</span>
                </h2>
                <button
                    onClick={load}
                    disabled={loading}
                    className="flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-300 transition-colors disabled:opacity-40"
                >
                    {loading
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <RotateCcw className="w-3 h-3" />
                    }
                    Actualizar
                </button>
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-4 justify-center">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Consultando backend...
                </div>
            )}

            {error && (
                <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    {error}
                </div>
            )}

            {!loading && !error && isEmpty && (
                <div className="text-xs text-slate-500 italic text-center py-4">
                    El backend no tiene contenido publicado. El simulador usa los datos internos.
                </div>
            )}

            {!loading && !error && !isEmpty && (
                <>
                    <p className="text-[11px] text-slate-600 mb-3">
                        {totalItems} elementos publicados
                        {isBilingualBlob && (
                            <span className="ml-2 text-indigo-400">· JSON bilingüe (es / en)</span>
                        )}
                    </p>
                    {localeUnpack ? (
                        <div className="space-y-6">
                            {['es', 'en'].map(lang => (
                                <div key={lang}>
                                    <p className="text-[11px] font-semibold text-indigo-300 uppercase tracking-wider mb-2">{lang}</p>
                                    <LocaleContentSections unpack={localeUnpack[lang]} emptyNote={emptyNote} />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <LocaleContentSections
                            unpack={{ skills, criteria, challenges, escenarios, sceneMoments }}
                            emptyNote={emptyNote}
                        />
                    )}
                </>
            )}
        </div>
    );
}

// ─── Wrapper con auth gate ────────────────────────────────────────────────────
export default function AdminPanelWithAuth() {
    const [authed, setAuthed] = useState(() => !!getStoredAdminPassword());
    if (!authed) return <AdminLogin onAuthed={() => setAuthed(true)} />;
    return <AdminPanel onLogout={() => { clearStoredAdminPassword(); setAuthed(false); }} />;
}

// ─── Panel principal ─────────────────────────────────────────────────────────
function AdminPanel({ onLogout }) {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('content'); // 'content' | 'surveys' | 'sessions'
    const { content, isLoadingContent, refresh } = useContent();
    const fileInputRef = useRef(null);
    // Referencia a la función de recarga de ContentPreview para actualizarla
    // después de publicar o resetear sin necesidad de desmontar el componente.
    const refreshPreviewRef = useRef(null);

    const [isDragging, setIsDragging] = useState(false);
    const [parsedFile, setParsedFile] = useState(null); // { name, result, errors, summary, hasBlockingErrors }
    const [status, setStatus] = useState(null); // { type: 'success'|'error'|'loading', message }
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    // Experiencia que estamos editando ahora mismo. 'ambiental' es el blob
    // por defecto (content.json — usado por este simulador). 'arcade' es el
    // blob del frontend lite (content-arcade.json). Se puede cambiar en
    // caliente; cada acción (subir, publicar, resetear, preview) usa el valor
    // seleccionado al momento.
    const [experience, setExperience] = useState('ambiental');
    const EXPERIENCES = [
        { value: 'ambiental', label: 'Ambiental (frontend principal)', blob: 'content.json' },
        { value: 'arcade', label: 'Arcade — Negociación (frontend lite)', blob: 'content-arcade.json' },
    ];
    const currentExpMeta = EXPERIENCES.find(e => e.value === experience) || EXPERIENCES[0];

    // ── Drag & Drop ──────────────────────────────────────────────────────────
    const handleDragOver = useCallback(e => { e.preventDefault(); setIsDragging(true); }, []);
    const handleDragLeave = useCallback(e => { e.preventDefault(); setIsDragging(false); }, []);
    const handleDrop = useCallback(async e => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (file) await processFile(file);
    }, []); // eslint-disable-line

    const handleFileChange = async e => {
        const file = e.target.files?.[0];
        if (file) await processFile(file);
        e.target.value = '';
    };

    const processFile = async (file) => {
        if (!file.name.endsWith('.xlsx')) {
            setStatus({ type: 'error', message: 'Solo se aceptan archivos .xlsx' });
            return;
        }
        setStatus({ type: 'loading', message: 'Analizando archivo...' });
        try {
            const parsed = await parseXlsx(file);
            setParsedFile({ name: file.name, ...parsed });
            setStatus(null);
        } catch (err) {
            setStatus({ type: 'error', message: `Error al leer el archivo: ${err.message}` });
        }
    };

    // ── Publicar ─────────────────────────────────────────────────────────────
    const handlePublish = async () => {
        if (!parsedFile || parsedFile.hasBlockingErrors) return;
        setStatus({ type: 'loading', message: 'Publicando contenido...' });
        try {
            await saveContent(parsedFile.result, { experience });
            await refresh();
            refreshPreviewRef.current?.();   // recarga la preview desde el blob
            setParsedFile(null);
            setStatus({ type: 'success', message: 'Contenido publicado correctamente. Los cambios están activos.' });
        } catch (err) {
            setStatus({ type: 'error', message: `Error al publicar: ${err.message}` });
        }
    };

    // ── Resetear ─────────────────────────────────────────────────────────────
    const handleReset = async () => {
        setShowResetConfirm(false);
        setStatus({ type: 'loading', message: 'Reseteando a fallback...' });
        try {
            await resetContent({ experience });
            await refresh();
            refreshPreviewRef.current?.();   // recarga la preview desde el blob
            setParsedFile(null);
            setStatus({ type: 'success', message: 'Contenido reseteado. El simulador usa ahora los datos internos.' });
        } catch (err) {
            setStatus({ type: 'error', message: `Error al resetear: ${err.message}` });
        }
    };

    const src = content?._source;

    return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%)' }}>
            {/* Header */}
            <div className="border-b border-slate-700/30 backdrop-blur-sm sticky top-0 z-10" style={{ background: 'rgba(15,23,42,0.85)' }}>
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center gap-1.5 text-slate-500 hover:text-white text-sm transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Volver al simulador
                        </button>
                        <div className="w-px h-4 bg-slate-700" />
                        <div>
                            <h1 className="text-base font-bold text-white leading-none">Gestor de contenido</h1>
                            <p className="text-[10px] text-slate-500 tracking-widest uppercase mt-0.5">Tech Lab · Admin</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {isLoadingContent && (
                            <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                Cargando estado...
                            </div>
                        )}
                        <button
                            onClick={onLogout}
                            title="Cerrar sesión admin"
                            className="flex items-center gap-1.5 text-slate-400 hover:text-white text-xs transition-colors px-2.5 py-1.5 rounded-md hover:bg-slate-800/60"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            Salir
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">

                {/* Tabs */}
                <div className="flex items-center gap-1 border-b border-slate-700/40">
                    <TabButton active={activeTab === 'content'} onClick={() => setActiveTab('content')} icon={FileText}>
                        Contenido
                    </TabButton>
                    <TabButton active={activeTab === 'sessions'} onClick={() => setActiveTab('sessions')} icon={MessageSquare}>
                        Sesiones
                    </TabButton>
                    <TabButton active={activeTab === 'surveys'} onClick={() => setActiveTab('surveys')} icon={BarChart3}>
                        Encuestas
                    </TabButton>
                </div>

                {activeTab === 'sessions' && <AdminSessionsTable />}
                {activeTab === 'surveys' && <AdminSurveysTable />}

                {activeTab === 'content' && (<>

                {/* Selector de experiencia (qué blob estamos editando) */}
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-4 flex-wrap">
                    <div className="flex-1 min-w-[260px]">
                        <h2 className="text-sm font-semibold text-amber-200 mb-1">Editando experiencia</h2>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                            Cada experiencia tiene su propio archivo en blob storage.
                            Las acciones de subida, publicación, reseteo y la previsualización de abajo
                            operan sobre <code className="text-amber-300">{currentExpMeta.blob}</code>.
                        </p>
                    </div>
                    <select
                        value={experience}
                        onChange={(e) => setExperience(e.target.value)}
                        className="px-3 py-2 rounded-lg bg-slate-900 border border-slate-700 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-amber-500/40"
                    >
                        {EXPERIENCES.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>

                {/* Estado actual — solo aplica al simulador de este frontend (ambiental). */}
                {experience === 'ambiental' && content && src && (
                    <div className="rounded-xl border border-slate-700/40 bg-slate-900/50 p-5">
                        <h2 className="text-sm font-semibold text-slate-300 mb-3">Estado actual del contenido</h2>
                        {content.isBilingual && src.es ? (
                            <div className="space-y-4">
                                {['es', 'en'].map(lang => (
                                    <div key={lang}>
                                        <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{lang}</p>
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            {[
                                                { label: 'Escenarios', key: 'escenarios' },
                                                { label: 'skill_config', key: 'skill_config' },
                                                { label: 'Scene moments', key: 'scene_moments' },
                                            ].map(({ label, key }) => (
                                                <div key={key} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/30">
                                                    <span className="text-xs text-slate-400">{label}</span>
                                                    <SourceBadge source={src[lang]?.[key]} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                {[
                                    { label: 'Skills',        key: 'skills' },
                                    { label: 'Criterios',     key: 'criteria' },
                                    { label: 'Challenges',    key: 'challenges' },
                                    { label: 'Escenarios',    key: 'escenarios' },
                                    { label: 'Scene moments', key: 'scene_moments' },
                                ].map(({ label, key }) => (
                                    <div key={key} className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/30">
                                        <span className="text-xs text-slate-400">{label}</span>
                                        <SourceBadge source={src[key]} />
                                    </div>
                                ))}
                            </div>
                        )}
                        {content.updated_at && (
                            <p className="text-[11px] text-slate-600 mt-3">
                                Última actualización: {new Date(content.updated_at).toLocaleString('es')}
                            </p>
                        )}
                    </div>
                )}

                {/* Previsualización del contenido real en el backend (GET /management/content) */}
                <ContentPreview experience={experience} onRefreshDone={fn => { refreshPreviewRef.current = fn; }} />

                {/* Notificación de estado */}
                {status && status.type !== 'loading' && (
                    <div className={`flex items-start gap-3 px-4 py-3.5 rounded-xl border text-sm ${
                        status.type === 'success'
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300'
                            : 'bg-red-500/10 border-red-500/20 text-red-300'
                    }`}>
                        {status.type === 'success'
                            ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                            : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                        }
                        <span className="flex-1">{status.message}</span>
                        <button onClick={() => setStatus(null)} className="opacity-60 hover:opacity-100">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {status?.type === 'loading' && (
                    <div className="flex items-center gap-3 px-4 py-3.5 rounded-xl border border-slate-700/40 bg-slate-800/40 text-sm text-slate-400">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {status.message}
                    </div>
                )}

                {/* Drop zone */}
                <div>
                    <h2 className="text-sm font-semibold text-slate-300 mb-3">Subir nuevo contenido (.xlsx)</h2>
                    <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`relative flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed cursor-pointer py-12 px-6 transition-all duration-300 ${
                            isDragging
                                ? 'border-indigo-500/70 bg-indigo-500/5 shadow-[0_0_40px_rgba(99,102,241,0.1)]'
                                : 'border-slate-700/50 hover:border-slate-600/70 bg-slate-900/30 hover:bg-slate-900/50'
                        }`}
                    >
                        <div className={`w-14 h-14 rounded-full flex items-center justify-center transition-all ${
                            isDragging ? 'bg-indigo-500/20' : 'bg-slate-800/60'
                        }`}>
                            <FileSpreadsheet className={`w-7 h-7 ${isDragging ? 'text-indigo-400' : 'text-slate-500'}`} />
                        </div>
                        <div className="text-center">
                            <p className="text-sm font-medium text-slate-300">
                                {isDragging ? 'Suelta aquí el archivo' : 'Arrastra tu .xlsx o haz clic para seleccionar'}
                            </p>
                            <p className="text-xs text-slate-600 mt-1">
                                Plantilla bilingüe: pares de hojas <span className="text-slate-400">Skills_ES / Skills_EN</span>,{' '}
                                <span className="text-slate-400">Criteria_ES / Criteria_EN</span>,{' '}
                                <span className="text-slate-400">Challenges_ES / Challenges_EN</span>,{' '}
                                <span className="text-slate-400">Escenarios_ES / Escenarios_EN</span>; opcional{' '}
                                <span className="text-slate-400">SceneMoments_ES / SceneMoments_EN</span>.
                                Mismo <span className="text-slate-400">id</span> de escenario y mismas <span className="text-slate-400">skill_slug</span> en ambos idiomas.
                            </p>
                        </div>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx"
                            className="hidden"
                            onChange={handleFileChange}
                        />
                    </div>
                </div>

                {/* Resumen del archivo parseado */}
                {parsedFile && (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                            <FileSpreadsheet className="w-3.5 h-3.5" />
                            <span className="truncate">{parsedFile.name}</span>
                            <button onClick={() => setParsedFile(null)} className="ml-auto hover:text-slate-300 transition-colors">
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <ParseSummary
                            summary={parsedFile.summary}
                            errors={parsedFile.errors}
                            hasBlockingErrors={parsedFile.hasBlockingErrors}
                            bilingual={parsedFile.bilingual}
                        />
                        <button
                            onClick={handlePublish}
                            disabled={parsedFile.hasBlockingErrors || status?.type === 'loading'}
                            className={`w-full py-3 rounded-xl font-medium text-sm transition-all ${
                                parsedFile.hasBlockingErrors || status?.type === 'loading'
                                    ? 'bg-slate-800 text-slate-600 cursor-not-allowed'
                                    : 'text-white hover:shadow-[0_0_25px_rgba(99,102,241,0.3)] hover:scale-[1.01]'
                            }`}
                            style={(!parsedFile.hasBlockingErrors && status?.type !== 'loading')
                                ? { background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }
                                : undefined
                            }
                        >
                            {status?.type === 'loading' ? 'Publicando...' : 'Publicar contenido'}
                        </button>
                    </div>
                )}

                {/* Acciones secundarias */}
                <div className="grid sm:grid-cols-2 gap-3">
                    <button
                        onClick={downloadEmptyTemplate}
                        className="flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl border border-slate-700/40 bg-slate-900/40 hover:bg-slate-800/60 hover:border-slate-600/50 transition-all text-sm text-slate-300"
                    >
                        <Download className="w-4 h-4 text-slate-500" />
                        Descargar plantilla vacía
                    </button>
                    <button
                        onClick={exportCurrentAsXlsx}
                        className="flex items-center justify-center gap-2.5 px-5 py-3.5 rounded-xl border border-slate-700/40 bg-slate-900/40 hover:bg-slate-800/60 hover:border-slate-600/50 transition-all text-sm text-slate-300"
                    >
                        <Upload className="w-4 h-4 text-slate-500 rotate-180" />
                        Exportar contenido actual
                    </button>
                </div>

                {/* Resetear a fallback */}
                <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-5">
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-sm font-semibold text-red-300">Resetear a contenido interno</h3>
                            <p className="text-xs text-slate-500 mt-1">
                                Elimina el contenido cargado desde el backend y vuelve a usar los datos internos del simulador (hardcoded en config.js). Esta acción no se puede deshacer desde aquí.
                            </p>
                        </div>
                        {!showResetConfirm ? (
                            <button
                                onClick={() => setShowResetConfirm(true)}
                                className="shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-all text-xs font-medium"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                Resetear
                            </button>
                        ) : (
                            <div className="flex gap-2 shrink-0">
                                <button
                                    onClick={() => setShowResetConfirm(false)}
                                    className="px-3 py-2 rounded-lg text-xs text-slate-400 hover:text-white border border-slate-700/40 hover:bg-slate-800 transition-all"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleReset}
                                    className="px-3 py-2 rounded-lg text-xs text-white bg-red-600 hover:bg-red-500 transition-all font-medium"
                                >
                                    Confirmar
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Nota de deploy */}
                <div className="text-[11px] text-slate-600 text-center pb-4">
                    Nota de deploy: si usas Static Web Apps, Nginx u otro hosting, configura rewrite <code className="bg-slate-800 px-1 rounded">/* → /index.html</code> para que <code className="bg-slate-800 px-1 rounded">/admin</code> funcione en recarga directa.
                </div>

                </>)}
            </div>
        </div>
    );
}

// Pestaña visual del header del panel
function TabButton({ active, onClick, icon: Icon, children }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                active
                    ? 'border-emerald-400 text-white'
                    : 'border-transparent text-slate-400 hover:text-white'
            }`}
        >
            {Icon && <Icon className="w-4 h-4" />}
            {children}
        </button>
    );
}
